import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, MessageCircle, Phone, PackageSearch, PhoneCall, FileText, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SITE, waGeneral } from "@/config/siteConfig";
import { trackEvent } from "@/lib/analytics";

const NEXT_STEPS = [
  { icon: Send, title: "We Received Your Details", text: "Your enquiry has reached our Kashmir travel team." },
  { icon: PhoneCall, title: "Expect Our Call", text: "A travel expert will call you on your number to understand your plans." },
  { icon: FileText, title: "Get Your Personalised Quote", text: "You'll receive a Kashmir itinerary built around your dates and budget." },
];

const ThankYouPage = () => {
  const location = useLocation();
  const name = location.state?.name;
  const packageName = location.state?.packageName;

  useEffect(() => {
    document.title = "Thank You | Book Kashmir";
    let robots = document.head.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");
    return () => robots.setAttribute("content", "index, follow");
  }, []);

  return (
    <main className="bg-[#0F2F24] py-20 sm:py-28" data-testid="thank-you-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
        <motion.div
          initial={{ scale: 0, rotate: -30 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 220, damping: 16 }}
          className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-full bg-[#D99B26]"
          data-testid="thank-you-check"
        >
          <Check className="h-10 w-10 text-[#0F2F24]" strokeWidth={3} />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <p className="text-xs font-mono uppercase tracking-[0.25em] font-semibold text-[#D99B26] mb-4">Enquiry Received</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight text-white text-balance" data-testid="thank-you-heading">
            {name ? <>Thank You, {name.split(" ")[0]}!</> : "Thank You!"}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed">
            {packageName
              ? <>Your enquiry for the <span className="text-[#F3D9A0] font-semibold">{packageName}</span> package has been received. Our Kashmir travel expert will call you shortly to plan your trip.</>
              : "Your Kashmir enquiry has been received. Our travel expert will call you shortly to plan your holiday."}
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
          {NEXT_STEPS.map(({ icon: Icon, title, text }, i) => (
            <motion.div key={title}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.12, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-xl bg-white/5 p-5 ring-1 ring-white/10" data-testid={`thank-you-step-${i + 1}`}>
              <Icon className="h-5 w-5 text-[#D99B26] mb-3" />
              <h2 className="text-sm font-bold text-white">{title}</h2>
              <p className="mt-1.5 text-xs text-white/65 leading-relaxed">{text}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.6 }}
          className="mt-12 flex flex-wrap justify-center gap-3"
        >
          <Button asChild className="bg-[#25D366] hover:bg-[#1EBE5D] text-white rounded-full px-6 h-11 font-semibold">
            <a href={waGeneral()} target="_blank" rel="noopener noreferrer" data-testid="thank-you-whatsapp-button"
              onClick={() => trackEvent("whatsapp_click", { location: "thank_you" })}>
              <MessageCircle className="mr-2 h-4 w-4" /> Chat on WhatsApp Now
            </a>
          </Button>
          <Button asChild variant="outline" className="rounded-full px-6 h-11 border-white/40 bg-white/5 text-white hover:bg-white/15">
            <a href={`tel:${SITE.phoneRaw}`} data-testid="thank-you-call-button"
              onClick={() => trackEvent("phone_click", { location: "thank_you" })}>
              <Phone className="mr-2 h-4 w-4" /> Call {SITE.phoneDisplay}
            </a>
          </Button>
          <Button asChild variant="ghost" className="rounded-full px-6 h-11 text-[#F3D9A0] hover:text-white hover:bg-white/10">
            <Link to="/kashmir-tour-packages" data-testid="thank-you-packages-link">
              <PackageSearch className="mr-2 h-4 w-4" /> Browse Packages
            </Link>
          </Button>
        </motion.div>
      </div>
    </main>
  );
};

export default ThankYouPage;
